'use client'

import { sendBeaconServerEvent } from 'analytics/events/beacon/sendBeaconServerEvent'
import {
	type AddToCartPayload,
	type MapAddToCartToBeaconProps,
	mapAddToCartToBeacon,
} from 'analytics/events/eec/addToCart/addToCart'
import {
	type AddToWishlistPayload,
	type MapAddToWishlistToBeaconProps,
	mapAddToWishlistToBeacon,
} from 'analytics/events/eec/addToWishlist/addToWishlist'
import {
	type BeginCheckoutPayload,
	type MapBeginCheckoutToBeaconProps,
	mapBeginCheckoutToBeacon,
} from 'analytics/events/eec/beginCheckout/beginCheckout'
import {
	type MapRemoveFromCartToBeaconProps,
	mapRemoveFromCartToBeacon,
	type RemoveFromCartPayload,
} from 'analytics/events/eec/removeFromCart/removeFromCart'
import {
	mapRemoveFromWishlistToBeacon,
	type RemoveFromWishlistPayload,
} from 'analytics/events/eec/removeFromWishlist/removeFromWishlist'
import {
	type MapSelectItemToBeaconProps,
	mapSelectItemToBeacon,
	type SelectItemPayload,
} from 'analytics/events/eec/selectItem/selectItem'
import {
	type MapViewCartToBeaconProps,
	mapViewCartToBeacon,
	type ViewCartPayload,
} from 'analytics/events/eec/viewCart/viewCart'
import {
	type MapViewItemToBeaconProps,
	mapViewItemToBeacon,
} from 'analytics/events/eec/viewItem/viewItem'
import {
	type MapViewItemListToBeaconProps,
	mapViewItemListToBeacon,
	type ViewItemListPayload,
} from 'analytics/events/eec/viewItemList/viewItemList'
import type { ViewItemPayload } from 'analytics/types/EEC'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useCallback, useMemo } from 'react'
import { useSession } from 'session/src/hooks/useSession'

export interface UseEECEvents {
	sendAddToCart: (params: AddToCartPayload) => Promise<void>
	sendBeginCheckout: (params: BeginCheckoutPayload) => void
	sendRemoveFromCart: (params: RemoveFromCartPayload) => Promise<void>
	sendSelectItem: (params: SelectItemPayload) => Promise<void>
	sendViewCart: (params: ViewCartPayload) => void
	sendViewItem: (params: ViewItemPayload) => Promise<void>
	sendViewItemList: (params: ViewItemListPayload) => void
	sendAddToWishlist: (params: AddToWishlistPayload) => Promise<void>
	sendRemoveFromWishlist: (params: RemoveFromWishlistPayload) => Promise<void>
}

export function useEECEvents(): UseEECEvents {
	const session = useSession()
	const masterData = useMasterData()

	const sendAddToCart = useCallback(
		async ({
			item,
			mngItems,
			location,
			listId,
			listName,
		}: AddToCartPayload): Promise<void> => {
			const payload: MapAddToCartToBeaconProps = {
				session,
				masterData,
				payload: {
					item,
					mngItems,
					location,
					listId,
					listName,
				},
			}

			sendBeaconServerEvent(session, await mapAddToCartToBeacon(payload))
		},
		[masterData, session]
	)

	const sendViewCart = useCallback(
		({ bagItemNumber, location, items }: ViewCartPayload): void => {
			const payload: MapViewCartToBeaconProps = {
				session,
				masterData,
				payload: {
					items,
					location,
					bagItemNumber,
				},
			}

			sendBeaconServerEvent(session, mapViewCartToBeacon(payload))
		},
		[masterData, session]
	)

	const sendRemoveFromCart = useCallback(
		async ({
			location,
			item,
			mngItems,
		}: RemoveFromCartPayload): Promise<void> => {
			const payload: MapRemoveFromCartToBeaconProps = {
				session,
				masterData,
				payload: {
					item,
					location,
					mngItems,
				},
			}

			sendBeaconServerEvent(session, await mapRemoveFromCartToBeacon(payload))
		},
		[masterData, session]
	)

	const sendBeginCheckout = useCallback(
		({ location, items }: BeginCheckoutPayload): void => {
			const payload: MapBeginCheckoutToBeaconProps = {
				session,
				masterData,
				payload: {
					items,
					location,
				},
			}

			sendBeaconServerEvent(session, mapBeginCheckoutToBeacon(payload))
		},
		[masterData, session]
	)

	const sendViewItem = useCallback(
		async ({ item }: ViewItemPayload): Promise<void> => {
			const payload: MapViewItemToBeaconProps = {
				session,
				masterData,
				payload: {
					item,
				},
			}

			sendBeaconServerEvent(session, await mapViewItemToBeacon(payload))
		},
		[masterData, session]
	)

	const sendSelectItem = useCallback(
		async ({
			item,
			location,
			listId,
			listName,
		}: SelectItemPayload): Promise<void> => {
			const payload: MapSelectItemToBeaconProps = {
				session,
				masterData,
				payload: {
					item,
					location,
					listId,
					listName,
				},
			}

			sendBeaconServerEvent(session, await mapSelectItemToBeacon(payload))
		},
		[masterData, session]
	)

	const sendViewItemList = useCallback(
		async ({ items, listId }: ViewItemListPayload) => {
			const payload: MapViewItemListToBeaconProps = {
				session,
				masterData,
				payload: {
					items,
					listId,
				},
			}

			sendBeaconServerEvent(session, mapViewItemListToBeacon(payload))
		},
		[masterData, session]
	)

	const sendAddToWishlist = useCallback(
		async ({ item, location, listId }: AddToWishlistPayload): Promise<void> => {
			const payload: MapAddToWishlistToBeaconProps = {
				session,
				masterData,
				payload: {
					item,
					location,
					listId,
				},
			}

			sendBeaconServerEvent(session, await mapAddToWishlistToBeacon(payload))
		},
		[masterData, session]
	)

	const sendRemoveFromWishlist = useCallback(
		async ({
			item,
			location,
			listId,
		}: RemoveFromWishlistPayload): Promise<void> => {
			const payload: MapAddToWishlistToBeaconProps = {
				session,
				masterData,
				payload: {
					item,
					location,
					listId,
				},
			}

			sendBeaconServerEvent(
				session,
				await mapRemoveFromWishlistToBeacon(payload)
			)
		},
		[masterData, session]
	)

	return useMemo(
		() => ({
			sendAddToCart,
			sendViewCart,
			sendRemoveFromCart,
			sendBeginCheckout,
			sendViewItem,
			sendSelectItem,
			sendViewItemList,
			sendAddToWishlist,
			sendRemoveFromWishlist,
		}),
		[
			sendAddToCart,
			sendViewCart,
			sendRemoveFromCart,
			sendBeginCheckout,
			sendViewItem,
			sendSelectItem,
			sendViewItemList,
			sendAddToWishlist,
			sendRemoveFromWishlist,
		]
	)
}
